import { resolveItemModelId } from './character-system';
import type { Equipment } from './types/game';

/** The parts of an item the hands rules read. */
export type HandsItem = Pick<Equipment, 'id' | 'itemDefinitionId' | 'modelId' | 'offHandModelId'> & {
    readonly combat?: Pick<NonNullable<Equipment['combat']>, 'grip'>;
};

/** Why a pair of hands can't hold what a move would leave in them. */
export type HandsConflict =
    /** A weapon that takes both hands goes in the main hand. */
    | 'both-hands-in-off-hand'
    /** Beside a weapon that takes both hands, the off hand stays empty. */
    | 'off-hand-occupied'
    /** The off-hand item had to move out, but no carried slot was free. */
    | 'no-room-for-off-hand';

/** Whether a weapon takes both hands: a two-handed grip, or a paired set. */
export function isBothHanded(item: HandsItem | null | undefined): boolean {
    const grip = item?.combat?.grip;
    return grip === 'two-handed' || grip === 'paired';
}

/**
 * Whether a main and off hand can hold these together. Checked on the
 * outcome of every equipment move, wherever it's applied (the client, the
 * host, the database), so all three agree. Undefined when they can.
 */
export function findHandsConflict(mainHand: HandsItem | null | undefined, offHand: HandsItem | null | undefined): HandsConflict | undefined {
    if (isBothHanded(offHand)) {
        return 'both-hands-in-off-hand';
    }
    if (isBothHanded(mainHand) && offHand) {
        return 'off-hand-occupied';
    }
    return undefined;
}

/** What a player reads when a move breaks the hands rules. */
export const HANDS_CONFLICT_MESSAGES: Readonly<Record<HandsConflict, string>> = {
    'both-hands-in-off-hand': 'A weapon that takes both hands goes in the main hand.',
    'off-hand-occupied': 'That needs both hands: the off hand must be empty.',
    'no-room-for-off-hand': 'That needs both hands, and there is no free carried slot for your off-hand item.',
};

/** What a move must also do about the off hand for the hands to stay valid. */
export type OffHandDisplacement<TItem extends HandsItem> =
    | { readonly kind: 'none' }
    /** Move `item` out of the off hand into a free carried slot. */
    | { readonly kind: 'displace'; readonly item: TItem }
    | { readonly kind: 'refuse'; readonly conflict: HandsConflict };

/**
 * Settles the hands a move would leave (`mainHand`, `offHand`). When the
 * move put a weapon that takes both hands into the main hand
 * (`mainHandChanged`), whatever is in the off hand makes way, moving to a
 * carried slot; anything else that breaks the hands rules is refused. The
 * client, the host and the server all apply it, so they agree.
 */
export function resolveOffHandDisplacement<TItem extends HandsItem>(
    mainHand: TItem | null | undefined,
    offHand: TItem | null | undefined,
    hasMainHandChanged: boolean,
): OffHandDisplacement<TItem> {
    const conflict = findHandsConflict(mainHand, offHand);
    if (conflict === undefined) {
        return { kind: 'none' };
    }
    if (conflict === 'off-hand-occupied' && hasMainHandChanged && offHand) {
        return { kind: 'displace', item: offHand };
    }
    return { kind: 'refuse', conflict };
}

/**
 * What the empty off-hand slot shows beside a weapon that takes both hands:
 * the off-hand model (a bow's quiver), or the weapon itself. A two-handed
 * weapon's is faded, as it only borrows the hand; a paired set's is shown
 * in full, as the off hand really holds its other half. Null when the main
 * hand leaves the off hand free.
 */
export function resolveOffHandPresentation(mainHand: HandsItem | null | undefined): { modelId: string; isFaded: boolean } | undefined {
    if (!mainHand || !isBothHanded(mainHand)) {
        return undefined;
    }
    return {
        modelId: mainHand.offHandModelId ?? resolveItemModelId(mainHand),
        isFaded: mainHand.combat?.grip === 'two-handed',
    };
}

/**
 * The lowest carried slot below `capacity` that holds nothing (null or
 * undefined), where an off-hand item makes way to; undefined when every
 * one is taken. The client, the host and the database all ask it.
 */
export function findFreeCarriedSlot(slots: ReadonlyArray<unknown>, capacity: number): number | undefined {
    for (let index = 0; index < capacity; index += 1) {
        if (slots[index] === null || slots[index] === undefined) {
            return index;
        }
    }
    return undefined;
}
